/* eslint-disable camelcase */
import React, {
  useState, useEffect, Dispatch, SetStateAction,
  useContext,
} from 'react';
import {
  Box, Button, Chip, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import DialogBox from '../DialogBox/DialogBox';
import FormField from '../FormField/FormField';
import {
  getAccountId,
  patchData,
  postData,
  getData,
} from '../../utils/utils';
import Dropdown from '../Dropdown/Dropdown';
import './LocationDialog.scss'; // Add this new SCSS
import PlacesAutocomplete from '../AddressField/AddressField';
import { DataContext } from '../../context/DataContext';

interface LocationFormData {
  name: string;
  address: {
    description?: string;
    [key: string]: any;
  };
  tags?: string[];
  currency?: string;
}

interface LocationFormDialogProps {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  mode: 'add' | 'edit';
  initialData?: any;
  organisationId: string;
  currentUser?: any;
  onSuccess: (mode: 'add' | 'edit') => void;
  onRequestArchive: (locationId: string) => void;
  refreshLocations: () => Promise<void>;
}

function LocationFormDialog({
  show,
  setShow,
  mode,
  initialData = {} as LocationFormData,
  organisationId,
  currentUser,
  onSuccess,
  onRequestArchive,
  refreshLocations,
}: LocationFormDialogProps) {
  const emptyForm: LocationFormData = {
    name: '',
    address: {},
    tags: [],
    currency: '',
  };
  const [currencies, setCurrencies] = useState<{ [key: string]: string }>({});
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [formData, setFormData] = useState<LocationFormData>(emptyForm);
  const [tagsInput, setTagsInput] = useState('');
  const { setMessagebar, setLoading } = useContext(DataContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      const tagsArray = Array.isArray(initialData.tags) ? initialData.tags : [];
      // Only include currency in formData for edit if initialData has it set.
      const editForm: LocationFormData = {
        name: initialData.name,
        address: initialData.address || {},
        tags: tagsArray,
      };
      if (initialData.currency !== undefined) {
        editForm.currency = initialData.currency;
      }
      setFormData(editForm);
      setTagsInput('');
    } else {
      // 🧹 Always reset for Add mode
      setFormData(emptyForm);
      setTagsInput('');
    }
  }, [mode, initialData, show]);

  // Fetch currencies once to avoid duplicate calls
  useEffect(() => {
    let mounted = true;
    const fetchCurrencies = async () => {
      setCurrenciesLoading(true);
      try {
        const response = await getData('/currencies');
        if (mounted && response && response.data) setCurrencies(response.data);
      } catch (err) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'Failed to fetch currencies. Please try again later.',
          severity: 'error',
        }));
      } finally {
        if (mounted) setCurrenciesLoading(false);
      }
    };
    fetchCurrencies();
    return () => { mounted = false; };
  }, []);

  // Prepare currency options for Dropdown
  const currencyOptions = Object.entries(currencies).map(([code, name]) => ({
    id: code,
    shortForm: code,
    title: ` ${name}`,
  }));

  const handleChange = (
    field: keyof LocationFormData,
    value: string | object,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // eslint-disable-next-line max-len
  const getUpdatedFieldsOnly = (): Partial<LocationFormData> => Object.entries(formData).reduce((acc, [key, value]) => {
    if (JSON.stringify(value) !== JSON.stringify(initialData[key])) {
      acc[key as keyof LocationFormData] = value;
    }
    return acc;
  }, {} as Partial<LocationFormData>);

  const toggleSwitchOrg = async (locationId: string) => {
    if (locationId) {
      try {
        const toggleResponse = await patchData(
          `/entities/${locationId}/org-switch?organisation_id=${currentUser?.organisation}`,
          { account_id: currentUser?.id, change_active_session: false },
        );

        if (toggleResponse.status === 400 || toggleResponse.status === 500) {
          setMessagebar((prev: any) => ({
            ...prev,
            show: true,
            text: 'Failed to switch entity',
            severity: 'error',
          }));
          return;
        }

        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'Successfully switched to the newly created entity.',
          severity: 'success',
        }));
      } catch (err) {
        setMessagebar((prev: any) => ({
          ...prev,
          show: true,
          text: 'Failed to update star status',
          severity: 'error',
        }));
      }
    }
  };

  const handleSubmit = async () => {
    if (tagsInput.trim().length > 0) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: `Press Enter to save "${tagsInput.trim()}" as a tag before submitting.`,
        severity: 'warning',
      }));
      return;
    }
    setShow(false);
    setLoading(true);
    const { name, address } = formData;
    if (!organisationId || !name || !address?.description) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Please fill all required fields',
        severity: 'error',
      }));
      return;
    }

    try {
      const { accountId } = getAccountId();
      const payload = {
        ...(mode === 'add' ? formData : getUpdatedFieldsOnly()),
        organisation_id: organisationId,
        is_archive: mode !== 'add',
        account_id: accountId,
      };

      // Prevent adding/updating currency on edit operations.
      if (mode !== 'add' && Object.prototype.hasOwnProperty.call(payload, 'currency')) {
        delete (payload as any).currency;
      }

      const response = mode === 'add'
        ? await postData('/entities', payload)
        : await patchData(
          `/entities/${initialData.id}?organisation_id=${organisationId}`,
          payload,
        );

      if (response.status === 201 || response.status === 204) {
        if (mode === 'add' && response?.data?.id) {
          Cookies.set('selectedOrganisation', response.data.id);
          Cookies.set('active_org_session', response.data.id);
          if (response.data.name) {
            Cookies.set('selectedOrganisationName', response.data.name);
          }
          if (response.data.currency) {
            Cookies.set('selectedOrganisationCurrency', response.data.currency);
          } else {
            Cookies.remove('selectedOrganisationCurrency');
          }
          await toggleSwitchOrg(response.data.id);
          onSuccess(mode);
          setShow(false);
          setFormData(emptyForm);
          setTagsInput('');
          await refreshLocations();
          navigate('/dashboard');
        } else {
          // Edit mode (204 No Content) — no response body, just refresh in place
          onSuccess(mode);
          setShow(false);
          setFormData(emptyForm);
          setTagsInput('');
          await refreshLocations();
        }
        return;
      }

      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: response.data?.error || 'Submission failed, please try again later.',
        severity: 'error',
      }));
    } catch (error) {
      setMessagebar((prev: any) => ({
        ...prev,
        show: true,
        text: 'Location submission failed, please try again after some time.',
        severity: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  const isModified = mode === 'edit' && JSON.stringify(formData) !== JSON.stringify(initialData);

  return (
    <DialogBox
      show={show}
      setShow={setShow}
      minWidth="350px"
      maxWidth="xs"
      setCustomClass
    >
      <Box className="location-dialog-wrapper">
        <div className="location-form-left">
          <div className="form-title">
            {mode === 'add' ? 'Add New Location' : 'Edit Location'}
          </div>
          <FormField
            error=""
            label="Organisation Name"
            type="text"
            value={formData.name}
            handleField={(e) => handleChange('name', e.target.value)}
          />
          <PlacesAutocomplete
            key={`${mode}-${show}`}
            error=""
            fetchFullDetails
            initialValue={formData.address?.description || ''}
            handleField={(addressObj) => handleChange('address', addressObj)}
          />
          {mode === 'add' && (
            (currenciesLoading)
              ? <div>Loading currencies...</div>
              : (
                <Dropdown
                  label="Currency"
                  values={currencyOptions}
                  handleField={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange('currency', e.target.value)}
                  disabled={currenciesLoading}
                />
              )
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#00aab3' }}>
              Grouping/Tags
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Add a tag and press Enter"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              onBlur={() => { /* keep value visible so user sees warning */ }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && tagsInput.trim()) {
                  e.preventDefault();
                  const newTag = tagsInput.trim().toLowerCase();
                  const tagExists = formData.tags?.some(
                    (t) => t.toLowerCase() === newTag.toLowerCase(),
                  );
                  if (!tagExists) {
                    const newTags = [...(formData.tags || []), newTag];
                    setFormData({ ...formData, tags: newTags });
                  }
                  setTagsInput('');
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '42px', // matches other fields
                },
              }}
            />
            {tagsInput.trim().length > 0 && (
              <Typography
                variant="caption"
                sx={{ color: '#e65100', mt: 0.5, display: 'block' }}
              >
                {`Press Enter to save "${tagsInput.trim()}" as a tag.`}
              </Typography>
            )}
            {(formData.tags || []).length > 0 && (
              <Box sx={{
                mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1,
              }}
              >
                {(formData.tags || []).map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => {
                      const updatedTags = (formData.tags || []).filter((t) => t !== tag);
                      setFormData({ ...formData, tags: updatedTags });
                      setTagsInput(''); // 🔄 clear input too
                    }}
                    deleteIcon={<CloseIcon style={{ color: '#fff' }} />}
                    sx={{
                      backgroundColor: '#61b9c3',
                      color: '#fff',
                      '& .MuiChip-deleteIcon': {
                        color: '#fff',
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <div className="form-btn">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {mode === 'edit' ? (
                <Button
                  startIcon={<i className="fa-solid fa-trash" />}
                  sx={{ color: '#21252D', fontWeight: 400 }}
                  onClick={() => {
                    if (mode === 'edit' && initialData?.id) {
                      if (initialData?.is_starred) {
                        setMessagebar((prev: any) => ({
                          ...prev,
                          show: true,
                          text: 'This entity is marked as a favorite and cannot be archived. Please remove it from your favorites or designate another entity as your favorite before proceeding.',
                          severity: 'error',
                        }));
                        setShow(false); // close dialog
                        return;
                      } if (initialData?.id === currentUser.active_org_session) {
                        setMessagebar((prev: any) => ({
                          ...prev,
                          show: true,
                          text: 'This entity is currently designated as the main entity and cannot be archived. Please set a different entity as the main entity before proceeding.',
                          severity: 'error',
                        }));
                        setShow(false); // close dialog
                        return;
                      }
                      onRequestArchive(initialData.id); // 👈 trigger confirm popup
                      setShow(false); // close dialog
                    }
                  }}
                >
                  ARCHIVE
                </Button>
              ) : (
                <Box sx={{ width: '100px' }} />
              )}

              <Box>
                <Button
                  startIcon={<i className="fa-solid fa-xmark" />}
                  sx={{ color: '#21252D', fontWeight: 400, mr: 1 }}
                  onClick={() => setShow(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={mode === 'edit' && !isModified}
                  startIcon={<i className="fa-solid fa-check" />}
                  sx={{ color: '#61b9c3', fontWeight: 400 }}
                  onClick={handleSubmit}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </div>
        </div>
      </Box>
    </DialogBox>
  );
}

export default LocationFormDialog;
